import { type ExperienceData } from '@atlassian/ufo-experimental/types';

import { type ResourceEntry, type ResourceTimingsConfig } from '../../../../../types';
import { getPerformanceObject } from '../../../utils/get-performance-object';
import { getWindowObject } from '../../../utils/get-window-object';
import { getResourceTimings } from '../../resource-timings-buffer';

const cacheableTypes = ['script', 'link'];
const resourceTypes = ['fetch', 'xmlhttprequest'];
const CACHE_NETWORK = 'network';
const CACHE_MEMORY = 'memory';
const CACHE_DISK = 'disk';

const calculateTransferType = (type: string, duration: number, size: number | void) => {
	if (!cacheableTypes.includes(type)) {
		return CACHE_NETWORK;
	}

	if ((size === undefined || size === 0) && duration === 0) {
		return CACHE_MEMORY;
	}
	if (size === 0 && duration > 0) {
		return CACHE_DISK;
	}
	if (size === undefined) {
		return null;
	}

	return CACHE_NETWORK;
};

const hasAccessToResourceSize = (
	url: string,
	type: string,
	entry: ResourceEntry,
	hasTimingHeaders: (url: string, entry: ResourceEntry) => boolean,
) => {
	return (
		!cacheableTypes.includes(type) ||
		url.includes('localhost') ||
		(!!getWindowObject() && url.includes(window.location.hostname)) ||
		hasTimingHeaders(url, entry)
	);
};

interface BasicResourceTiming {
	duration: number;
	fetchStart: number;
	startTime: number;
	type: string;
	workerStart: number;
}

interface CacheableResourceTiming extends BasicResourceTiming {
	transferType: string;
	ttfb?: number;
}

interface NonCacheableResourceTiming extends BasicResourceTiming {
	size: number;
	ttfb: number;
}

export type ResourceTiming =
	| BasicResourceTiming
	| NonCacheableResourceTiming
	| CacheableResourceTiming;

const getReportedInitiatorTypes = (xhrEnabled: boolean) => {
	if (xhrEnabled) {
		return ['script', 'link', 'fetch', 'xmlhttprequest'];
	}
	return ['script', 'link', 'fetch'];
};

const evaluateAccessToResourceTimings = (_url: string, entry: ResourceEntry) => {
	return !(entry.responseStart === 0 && entry.startTime > entry.responseStart);
};

const getSizeObject = (size?: number | void) => (size !== undefined ? { size } : null);

const getNetworkData = (
	item: ResourceEntry,
	eventStart: number,
	hasTimingHeaders: (
		url: string,
		entry: ResourceEntry,
	) => boolean = evaluateAccessToResourceTimings,
) => {
	const { name, duration, transferSize, initiatorType, responseStart } = item;

	const ttfb = Math.round(responseStart - eventStart);

	if (!hasAccessToResourceSize(name, initiatorType, item, hasTimingHeaders)) {
		return {};
	}

	if (cacheableTypes.includes(initiatorType)) {
		const transferType = calculateTransferType(initiatorType, duration, transferSize);

		if (!transferType) {
			return { ttfb, ...getSizeObject(transferSize) };
		}

		if (transferType !== CACHE_NETWORK) {
			return { transferType };
		}

		return { ttfb, transferType, ...getSizeObject(transferSize) };
	}

	return { ttfb, ...getSizeObject(transferSize) };
};

export const resourceTimings = (
	data: ExperienceData,
	resourceTimings: ResourceTimingsConfig | null,
	performance: Performance | undefined = getPerformanceObject() || undefined,
): {
	'timings:resource': {
		[key: string]: ResourceTiming;
	};
} | null => {
	if (data.result.startTime === null || !resourceTimings?.sanitiseEndpoints) {
		return null;
	}

	const resources = getResourceTimings(data.result.startTime, performance);

	if (!resources?.length) {
		return null;
	}

	const resourceTiming: { [key: string]: ResourceTiming } = {};
	const { xhrFilter } = resourceTimings;
	const reportedInitiatorTypes = getReportedInitiatorTypes(!!xhrFilter);

	resources.forEach((item) => {
		if (!reportedInitiatorTypes.includes(item.initiatorType)) {
			return;
		}

		const { name, startTime, duration, workerStart, fetchStart, initiatorType } = item;

		if (!name || data.result.startTime === null) {
			return;
		}

		if (
			initiatorType === 'xmlhttprequest' &&
			(xhrFilter === undefined || xhrFilter(name) === false)
		) {
			return;
		}

		const url = resourceTypes.includes(initiatorType)
			? resourceTimings.sanitiseEndpoints(name)
			: resourceTimings.mapResources(name);

		if (!url || resourceTiming[url]) {
			return;
		}

		resourceTiming[url] = {
			startTime: Math.round(startTime - data.result.startTime),
			duration: Math.round(duration),
			workerStart: Math.max(Math.round(workerStart - data.result.startTime), 0),
			fetchStart: Math.max(Math.round(fetchStart - data.result.startTime), 0),
			type: initiatorType,
			...getNetworkData(item, data.result.startTime, resourceTimings.hasTimingHeaders),
		} as ResourceTiming;
	});

	return {
		'timings:resource': resourceTiming,
	};
};
